import DataSource from "./DataSource";
import {Form} from "@/utils/Form";
import {
    UpdateGatewayRoutingMutation,
    UpdateGatewayRoutingMutationVariables,
    useUpdateGatewayRoutingMutation,
} from "@/components/gateway/routing/mutations.generated";
import {useState} from "react";
import {GatewayRouting} from "@/api";
import isEqual from "lodash/isEqual";

type Routings = {
    [gatewayId: number]: GatewayRouting[];
};

export default class RoutingForm extends Form<UpdateGatewayRoutingMutation, UpdateGatewayRoutingMutationVariables> {
    readonly dataSource: DataSource;

    readonly routings: Routings;

    private setRoutings: (routings: Routings) => void;
    private destinationIds: number[];
    private gatewayIds: number[];

    constructor(dataSource: DataSource, destinationIds: number[], gatewayIds: number[]) {
        super("updateGatewayRouting");
        this.hooks(useUpdateGatewayRoutingMutation, dataSource.initialData);
        this.dataSource = dataSource;
        this.destinationIds = destinationIds;
        this.gatewayIds = gatewayIds;
        [this.routings, this.setRoutings] = useState<Routings>(this.routingsFromInitialData());
    }

    private routingsFromInitialData(): Routings {
        const routings: Routings = {};

        for (const routing of this.dataSource.initialData.gatewayRoutings.sort(
            (a, b) => a.destinationId - b.destinationId,
        )) {
            if (!routings[routing.gatewayId]) routings[routing.gatewayId] = [];
            routings[routing.gatewayId].push(structuredClone(routing));
        }

        for (const gatewayId of this.gatewayIds) {
            if (!routings[gatewayId]) routings[gatewayId] = [];
        }

        return routings;
    }

    get routingsAsArray(): GatewayRouting[] {
        const routings: GatewayRouting[] = [];
        for (const gatewayId of this.gatewayIds) {
            for (const routing of this.routings[gatewayId].sort((a, b) => a.destinationId - b.destinationId)) {
                routings.push(routing);
            }
        }
        return structuredClone(routings);
    }

    addNextRouting(): void {
        const nextGatewayId = this.gatewayIds.find(
            (gatewayId) => !this.routings[gatewayId] || this.routings[gatewayId].length < this.destinationIds.length,
        );
        if (nextGatewayId === undefined) return;

        const copy = structuredClone(this.routings);

        const routing: GatewayRouting = {
            comment: "",
            destinationId: this.destinationIds.find(
                (destinationId) =>
                    !this.routings[nextGatewayId].map((routing) => routing.destinationId).includes(destinationId),
            ),
            gatewayId: nextGatewayId,
            priority: 1,
        };

        copy[nextGatewayId].push(routing);
        this.setRoutings(copy);
    }

    removeRouting(gatewayId: number, destinationId: number): void {
        const copy = structuredClone(this.routings);
        copy[gatewayId] = copy[gatewayId]?.filter((routing) => routing.destinationId !== destinationId);
        this.setRoutings(copy);
    }

    updateRouting(from: GatewayRouting, to: Partial<GatewayRouting>): void {
        const copy = structuredClone(this.routings);
        const routing = copy[from.gatewayId].find((routing) => routing.destinationId === from.destinationId);
        if (routing === undefined) return;

        if (to.gatewayId !== undefined) {
            copy[from.gatewayId] = copy[from.gatewayId].filter((routing) => !isEqual(routing, from));
            if (copy[to.gatewayId] === undefined) copy[to.gatewayId] = [];
            copy[to.gatewayId].push(routing);
        }

        if (to.gatewayId !== undefined || to.destinationId !== undefined) {
            if (this.canAddNewRouting(to.gatewayId ?? from.gatewayId)) {
                routing.gatewayId = to.gatewayId ?? from.gatewayId;
                if (this.canAddNewRouting(to.gatewayId ?? from.gatewayId, to.destinationId ?? from.destinationId)) {
                    routing.destinationId = to.destinationId ?? from.destinationId;
                } else {
                    routing.destinationId = this.getAvailableDestinationsForGateway(to.gatewayId ?? from.gatewayId)[0];
                } // not necessary
            } else {
                return;
            }
        }

        if (to.comment !== undefined) routing.comment = to.comment;
        if (to.priority !== undefined) routing.priority = to.priority;

        this.setRoutings(copy);
    }

    canAddNewRouting(
        gatewayId: number | undefined = undefined,
        destinationId: number | undefined = undefined,
    ): boolean {
        const gatewaysToCheck = gatewayId === undefined ? this.gatewayIds : [gatewayId];

        for (const gatewayId of gatewaysToCheck) {
            if (this.routings[gatewayId] === undefined) return true;
            if (destinationId === undefined && this.routings[gatewayId].length < this.destinationIds.length)
                return true;
            if (
                destinationId !== undefined &&
                !this.routings[gatewayId].map((routing) => routing.destinationId).includes(destinationId)
            )
                return true;
        }

        return false;
    }

    get gatewaysWithEmptySpace(): number[] {
        return this.gatewayIds.filter((gatewayId) => this.canAddNewRouting(gatewayId));
    }

    getAvailableDestinationsForGateway(gatewayId: number): number[] {
        return this.destinationIds.filter(
            (destinationId) =>
                !this.routings[gatewayId]?.map((routing) => routing.destinationId).includes(destinationId),
        );
    }

    getAvailableGatewaysForDestination(destinationId: number): number[] {
        return this.gatewayIds.filter(
            (gatewayId) => !this.routings[gatewayId]?.map((routing) => routing.destinationId).includes(destinationId),
        );
    }

    override get hasChanged(): boolean {
        if (this.dataSource === undefined) return false;
        console.log("has changed?", this.routings, this.routingsFromInitialData());
        return isEqual(this.routings, this.routingsFromInitialData()) === false;
    }

    override reset(): void {
        this.setRoutings(this.routingsFromInitialData());
    }

    protected override get filteredVariables(): UpdateGatewayRoutingMutationVariables {
        return {
            domainId: this.dataSource.initialData.domainId,
            gatewayRoutings: this.routingsAsArray,
        };
    }
}
